// ** React Imports
import { ReactNode, useEffect, useRef } from 'react';

// ** MUI Imports
import { styled, useTheme } from '@mui/material/styles';
import MuiSwipeableDrawer, { SwipeableDrawerProps } from '@mui/material/SwipeableDrawer';

// ** Type Import
import { Settings } from 'src/@core/context/settingsContext';
import { useDynamicTheme } from 'src/@core/theme/DynamicTheme';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba';

interface Props {
  hidden: boolean;
  navWidth: number;
  navHover: boolean;
  settings: Settings;
  navVisible: boolean;
  children: ReactNode;
  collapsedNavWidth: number;
  navigationBorderWidth: number;
  setNavHover: (values: boolean) => void;
  setNavVisible: (value: boolean) => void;
  saveSettings: (values: Settings) => void;
}

const SwipeableDrawer = styled(MuiSwipeableDrawer)<SwipeableDrawerProps>({
  overflowX: 'hidden',
  transition: 'width .25s ease-in-out',
  '& ul': {
    listStyle: 'none'
  },
  '& .MuiListItem-gutters': {
    paddingLeft: 4,
    paddingRight: 4
  },
  '& .MuiDrawer-paper': {
    left: 'unset',
    right: 'unset',
    overflowX: 'hidden',
    transition: 'width .25s ease-in-out, box-shadow .25s ease-in-out'
  }
});

const Drawer = (props: Props) => {
  // ** Props
  const {
    hidden,
    children,
    navHover,
    navWidth,
    settings,
    navVisible,
    setNavHover,
    saveSettings,
    setNavVisible,
    collapsedNavWidth,
    navigationBorderWidth
  } = props;

  // ** Hook
  const prod = useGetProductLevel();
  const theme = useTheme();
  const { userTheme } = useDynamicTheme();

  // ** Vars
  const { skin, navCollapsed } = settings;
  const applyMyPortalTheme = prod === Products.MyPortal && userTheme && theme.palette.mode === 'light';
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drawerPaperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (navCollapsed && hidden) {
      saveSettings({ ...settings, navCollapsed: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden]);

  useEffect(() => {
    return () => {
      if (enterTimeoutRef.current) {
        clearTimeout(enterTimeoutRef.current);
      }

      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  const setNavHoverSafely = (value: boolean) => {
    if (navHover !== value) {
      setNavHover(value);
    }
  };

  const scheduleNavHover = (value: boolean) => {
    if (!navCollapsed) {
      setNavHoverSafely(value);

      return;
    }

    if (value) {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }

      if (enterTimeoutRef.current) {
        clearTimeout(enterTimeoutRef.current);
      }

      enterTimeoutRef.current = setTimeout(() => {
        if (!drawerPaperRef.current || !drawerPaperRef.current.matches(':hover')) {
          return;
        }

        setNavHoverSafely(true);
      }, 40);

      return;
    }

    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
    }

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }

    leaveTimeoutRef.current = setTimeout(() => {
      if (drawerPaperRef.current && drawerPaperRef.current.matches(':hover')) {
        return;
      }

      setNavHoverSafely(false);
    }, 90);
  };

  const drawerColor = () => {
    if (skin === 'semi-dark' && theme.palette.mode === 'light') {
      if (applyMyPortalTheme) {
        return {
          '& .MuiTypography-root, & .MuiSvgIcon-root': {
            color: userTheme?.iconFontColour
              ? `${hexToRGBA(userTheme?.iconFontColour, 0.87)}`
              : `rgba(${theme.palette.customColors.dark}, 0.87)`
          }
        };
      }

      return {
        '& .MuiTypography-root, & .MuiSvgIcon-root': {
          color: `rgba(${theme.palette.customColors.dark}, 0.87)`
        }
      };
    } else if (skin === 'semi-dark' && theme.palette.mode === 'dark') {
      return {
        '& .MuiTypography-root, & .MuiSvgIcon-root': {
          color: `rgba(${theme.palette.primary.main}, 0.87)`
        }
      };
    } else return {};
  };

  const drawerBgColor = () => {
    if (applyMyPortalTheme) {
      return {
        backgroundColor: userTheme?.navbarColour ? userTheme?.navbarColour : theme.palette.vigoColors.primaryLight
      };
    }

    return { backgroundColor: theme.palette.vigoColors.primaryLight };
  };

  // Drawer Props for Mobile & Tablet screens
  const MobileDrawerProps = {
    open: navVisible,
    onOpen: () => {
      setNavVisible(true);
      setNavHoverSafely(true);
    },
    onClose: () => {
      setNavVisible(false);
      setNavHoverSafely(false);
    },
    onMouseEnter: () => {
      scheduleNavHover(true);
    },
    onMouseLeave: () => {
      scheduleNavHover(false);
    },
    ModalProps: {
      keepMounted: true // Better open performance on mobile.
    }
  };

  // Drawer Props for Desktop screens
  const DesktopDrawerProps = {
    open: true,
    onOpen: () => null,
    onClose: () => null,
    onMouseEnter: () => {
      scheduleNavHover(true);
    },
    onMouseLeave: () => {
      scheduleNavHover(false);
    }
  };

  return (
    <SwipeableDrawer
      className="layout-vertical-nav"
      variant={hidden ? 'temporary' : 'permanent'}
      {...(hidden ? { ...MobileDrawerProps } : { ...DesktopDrawerProps })}
      PaperProps={{ ref: drawerPaperRef, sx: { width: navCollapsed && !navHover ? collapsedNavWidth : navWidth } }}
      sx={{
        width: navCollapsed ? collapsedNavWidth : navWidth,
        '& .MuiDrawer-paper': {
          ...drawerColor(),
          ...drawerBgColor(),
          ...(!hidden && navCollapsed && navHover ? { boxShadow: 9 } : {}),
          borderRight: navigationBorderWidth === 0 ? 0 : `${navigationBorderWidth}px solid ${theme.palette.divider}`
        },
        display: 'flex'
      }}>
      {children}
    </SwipeableDrawer>
  );
};

export default Drawer;
